<template>
  <div
    ref="wrapper"
    class="lg:fixed lg:h-full lg:left-0 flex flex-col w-full sidebar-transition group/wrapper lg:shadow-none lg:px-4 lg:pb-10 lg:bg-theme-paper lg:border-r lg:border-grey-300 dark:lg:border-theme-divider lg:border-opacity-20 lg:top-0 lg:z-20 lg:w-sidebar-nav lg:[&.collapsed]:w-sidebar-nav-collapsed lg:overflow-y-visible shrink-0 lg:[&.collapsed]:px-0"
  >
    <div class="hidden lg:flex items-center lg:h-[85px] border-b border-divider">
      <div
        class="relative items-center justify-between hidden w-full pl-4 group/logos group-[&.collapsed]/wrapper:justify-center lg:flex group-[&.collapsed]/wrapper:pl-0"
      >
        <!-- Logo -->
        <div
          class="content-transition group-[&.collapsed]/wrapper:flex group-[&.collapsed]/wrapper:items-center group-[&.collapsed]/wrapper:justify-center mr-auto group-[&.collapsed]/wrapper:mx-auto"
        >
          <ApplicationLogo class="justify-center" with-wordmark :is-collapsed="isCollapsed" />
        </div>
        <!-- Sidebar toggle button -->
        <button
          @click="handleSidebarWidth"
          class="flex items-center justify-center outline-none cursor-pointer button-transition shrink-0 hover:scale-110 active:scale-95 dark:text-white"
          :class="isCollapsed ? 'absolute -right-1/2 w-5 h-5 top-1/2 -translate-y-1/2' : 'w-5 h-5'"
        >
          <Icon type="menu-toggler" />
        </button>
      </div>
    </div>
    <div
      class="flex items-center flex-col self-stretch gap-2 w-full overflow-hidden sidebar-transition group-[&.collapsed]/wrapper:w-16 lg:mx-auto lg:items-start lg:p-2 lg:h-full lg:justify-start lg:flex-col lg:grow lg:shrink lg:basis-0"
    >
      <!-- <div
        class="items-center self-stretch justify-start hidden pt-4 pb-2 pl-3 rounded-lg lg:block"
      >
        <div
          class="text-theme-disabled content-transition dark:text-theme-disabled text-[0.6875rem] font-bold uppercase leading-[1.125rem] group-[&.collapsed]/wrapper:opacity-0"
        >
          {{ $t('Management') }}
        </div>
      </div> -->
      <div class="w-full" data-testid="org-switcher-row">
        <OrgSwitcher />
      </div>

      <Link
        href="/dashboard"
        :class="isActive('/dashboard') ? css.bg_active : css.bg_inactive"
        class="inline-flex items-center justify-start flex-shrink-0 w-full h-6 lg:h-[32px] relative px-6 menu-item-transition group overflow-hidden hover:scale-[1.02] active:scale-[0.98] lg:px-2"
      >
        <div
          class="flex justify-center items-center opacity-0 invisible left-0 truncate text-theme-disabled icon-slide-transition absolute group-[&.collapsed]/wrapper:opacity-100 group-[&.collapsed]/wrapper:visible group-[&.collapsed]/wrapper:translate-x-0 pl-3"
        >
          <Icon type="nav-dashboard" :width="24" :height="24" />
        </div>
        <div
          class="inline-flex truncate content-transition grow shrink basis-0 group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
        >
          <div
            class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
            :class="isActive('/dashboard') ? css.label_active : css.label"
          >
            {{ $t('Dashboard') }}
          </div>
        </div>
      </Link>
      <!-- Property -->
      <Link
        v-if="hasPermissions(USER_PERMISSIONS.HR) || $page.props.canViewProperty"
        href="/property"
        :class="isActive('/property') ? css.bg_active : css.bg_inactive"
        class="inline-flex items-center justify-start flex-shrink-0 w-full h-6 lg:h-[32px] relative px-6 menu-item-transition group overflow-hidden hover:scale-[1.02] active:scale-[0.98] lg:px-2"
      >
        <div
          class="flex justify-center items-center opacity-0 invisible left-0 truncate icon-slide-transition absolute group-[&.collapsed]/wrapper:opacity-100 group-[&.collapsed]/wrapper:visible group-[&.collapsed]/wrapper:translate-x-0 pl-3"
        >
          <Icon type="nav-property" :width="24" :height="24" />
        </div>
        <div
          class="inline-flex truncate content-transition grow shrink basis-0 group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
        >
          <div
            class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
            :class="isActive('/property') ? css.label_active : css.label"
          >
            {{ $t('Property') }}
          </div>
        </div>
      </Link>

      <div class="w-full shrink-0" ref="mobileJobsMenu">
        <span
          class="inline-flex items-center justify-start flex-shrink-0 w-full h-6 lg:h-[32px] relative px-6 menu-item-transition group overflow-hidden hover:scale-[1.02] active:scale-[0.98] lg:px-2 hover:cursor-pointer"
          :class="[isJobListUrl ? css.bg_active : css.bg_inactive]"
          @click.prevent="toggleJobs"
        >
          <div
            class="flex justify-center items-center opacity-0 invisible left-0 truncate icon-slide-transition absolute group-[&.collapsed]/wrapper:opacity-100 group-[&.collapsed]/wrapper:visible group-[&.collapsed]/wrapper:translate-x-0 pl-3"
          >
            <Icon type="nav-jobs" :width="24" :height="24" />
          </div>
          <div
            class="inline-flex truncate content-transition grow shrink basis-0 group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
          >
            <Link
              :href="route('job-list', 'active')"
              class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
              :class="isJobListUrl ? css.label_active : css.label"
            >
              {{ $t('Jobs') }}
            </Link>
          </div>
          <div
            v-if="countUnreadMessages > 0"
            class="w-fit px-1.5 py-1 bg-system-dark bg-opacity-20 rounded-md content-transition justify-start items-center gap-1.5 flex group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
          >
            <div class="text-xs font-bold leading-tight text-center text-system-dark">
              {{ formatBadgeCount(countUnreadMessages) }}
            </div>
          </div>
          <div
            class="flex items-center justify-center ml-auto transition-transform group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
            :class="showJobs ? 'rotate-0' : '-rotate-90'"
          >
            <div class="relative w-4 h-4">
              <Icon type="arrow-down" />
            </div>
          </div>
        </span>

        <!-- Mobile Submenu Items -->
        <div
          class="grid group-[&.collapsed]/wrapper:invisible group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:h-0 content-transition"
        >
          <Link
            v-if="showJobs"
            href="/job-list/active"
            class="flex items-center pl-4 overflow-hidden text-left menu-item-transition bg-opacity-0 h-9 lg:py-1.5 lg:h-auto hover:pl-5 lg:py-1.5 lg:h-auto"
          >
            <div class="flex items-center justify-center">
              <div class="flex items-center justify-center w-6 h-6">
                <div :class="isActive('/job-list/active') ? css.bull_active : css.bull"></div>
              </div>
            </div>
            <div class="inline-flex flex-col truncate transition-all grow">
              <div
                class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
                :class="isActive('/job-list/active') ? css.label_active_sub : css.label"
              >
                {{ $t('Active') }}
              </div>
            </div>
          </Link>

          <Link
            v-if="showJobs"
            href="/job-list/draft"
            class="flex items-center pl-4 overflow-hidden text-left menu-item-transition bg-opacity-0 h-9 lg:py-1.5 lg:h-auto hover:pl-5"
          >
            <div class="flex items-center justify-center">
              <div class="flex items-center justify-center w-6 h-6">
                <div :class="isActive('/job-list/draft') ? css.bull_active : css.bull"></div>
              </div>
            </div>
            <div class="inline-flex flex-col truncate transition-all grow">
              <div
                class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
                :class="isActive('/job-list/draft') ? css.label_active_sub : css.label"
              >
                {{ $t('Drafts') }}
              </div>
            </div>
          </Link>

          <Link
            v-if="showJobs"
            href="/job-list/archived"
            class="flex items-center pl-4 overflow-hidden text-left menu-item-transition bg-opacity-0 h-9 lg:py-1.5 lg:h-auto hover:pl-5"
          >
            <div class="flex items-center justify-center">
              <div class="flex items-center justify-center w-6 h-6">
                <div :class="isActive('/job-list/archived') ? css.bull_active : css.bull"></div>
              </div>
            </div>
            <div class="inline-flex flex-col truncate transition-all grow">
              <div
                class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
                :class="isActive('/job-list/archived') ? css.label_active_sub : css.label"
              >
                {{ $t('Archived') }}
              </div>
            </div>
          </Link>
        </div>
      </div>

      <div class="hidden shrink-0">
        <Dropdown
          contentClasses="py-1 bg-grey-8 dark:bg-primary !ring-0 !rounded-t-none"
          overlayClasses="!mt-0"
          width="full"
          align="left"
          @menuStateChange="handleJobsDropdownChange"
        >
          <template #trigger>
            <Link
              href="/job-list/active"
              class="inline-flex items-center justify-start flex-shrink-0 w-full h-6 lg:h-[40px] gap-1 lg:px-2 menu-item-transition min-w-28 group overflow-hidden hover:scale-[1.02] active:scale-[0.98]"
              :class="[isJobListUrl ? css.bg_active : css.bg_inactive]"
            >
              <div
                class="inline-flex truncate transition-opacity grow shrink basis-0 group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
              >
                <div
                  class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
                  :class="isJobListUrl ? css.label_active : css.label"
                >
                  {{ $t('Jobs') }}
                </div>
              </div>
              <div
                v-if="countUnreadMessages > 0"
                class="w-fit px-1.5 py-1 bg-system-dark bg-opacity-20 rounded-md content-transition justify-start items-center gap-1.5 flex group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
              >
                <div class="text-xs font-bold leading-tight text-center text-system-dark">
                  {{ formatBadgeCount(countUnreadMessages) }}
                </div>
              </div>
              <div
                class="flex items-center justify-center transition-transform group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
                :class="showJobs ? 'rotate-0' : '-rotate-90'"
              >
                <div class="relative w-4 h-4">
                  <Icon type="arrow-down" />
                </div>
              </div>
            </Link>
          </template>

          <template #content>
            <div>
              <Link
                href="/job-list/active"
                class="flex items-center self-stretch justify-start w-full overflow-hidden menu-item-transition bg-opacity-0 h-9 lg:py-1.5 lg:h-auto hover:bg-grey-16 dark:hover:bg-dark-neutral hover:pl-1"
              >
                <div class="flex items-center justify-center">
                  <div class="flex items-center justify-center w-6 h-6">
                    <div :class="isActive('/job-list/active') ? css.bull_active : css.bull"></div>
                  </div>
                </div>
                <div class="inline-flex flex-col truncate transition-all grow">
                  <div
                    class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
                    :class="isActive('/job-list/active') ? css.label_active_sub : css.label"
                  >
                    {{ $t('Active') }}
                  </div>
                </div>
              </Link>

              <Link
                href="/job-list/draft"
                class="flex items-center self-stretch justify-start w-full overflow-hidden menu-item-transition bg-opacity-0 h-9 lg:py-1.5 lg:h-auto hover:bg-grey-16 dark:hover:bg-dark-neutral hover:pl-1"
              >
                <div class="flex items-center justify-center">
                  <div class="flex items-center justify-center w-6 h-6">
                    <div :class="isActive('/job-list/draft') ? css.bull_active : css.bull"></div>
                  </div>
                </div>
                <div class="inline-flex flex-col truncate transition-all grow">
                  <div
                    class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
                    :class="isActive('/job-list/draft') ? css.label_active_sub : css.label"
                  >
                    {{ $t('Drafts') }}
                  </div>
                </div>
              </Link>

              <Link
                href="/job-list/archived"
                class="flex items-center self-stretch justify-start w-full overflow-hidden menu-item-transition bg-opacity-0 h-9 lg:py-1.5 lg:h-auto hover:bg-grey-16 dark:hover:bg-dark-neutral hover:pl-1"
              >
                <div class="flex items-center justify-center">
                  <div class="flex items-center justify-center w-6 h-6">
                    <div :class="isActive('/job-list/archived') ? css.bull_active : css.bull"></div>
                  </div>
                </div>
                <div class="inline-flex flex-col truncate transition-all grow">
                  <div
                    class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
                    :class="isActive('/job-list/archived') ? css.label_active_sub : css.label"
                  >
                    {{ $t('Archived') }}
                  </div>
                </div>
              </Link>

              <Link
                href="/job-list/duplicate"
                v-if="hasPermissions([USER_PERMISSIONS.HR])"
                class="flex items-center self-stretch justify-start w-full overflow-hidden menu-item-transition bg-opacity-0 h-9 lg:py-1.5 lg:h-auto hover:bg-grey-16 dark:hover:bg-dark-neutral hover:pl-1"
              >
                <div class="flex items-center justify-center">
                  <div class="flex items-center justify-center w-6 h-6">
                    <div
                      :class="isActive('/job-list/duplicate') ? css.bull_active : css.bull"
                    ></div>
                  </div>
                </div>
                <div class="inline-flex flex-col truncate transition-all grow">
                  <div
                    class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
                    :class="isActive('/job-list/duplicate') ? css.label_active_sub : css.label"
                  >
                    {{ $t('Duplicate Jobs') }}
                  </div>
                </div>
              </Link>
            </div>
          </template>
        </Dropdown>
      </div>

      <Link
        v-if="hasPermissions(USER_PERMISSIONS.HR)"
        :href="route('ai')"
        :class="
          isActive('/ai') || isActive('/ai/loading') || isActive('/ai/company')
            ? css.bg_active
            : css.bg_inactive
        "
        class="inline-flex items-center justify-start flex-shrink-0 w-full h-6 lg:h-[32px] relative px-6 menu-item-transition group overflow-hidden hover:scale-[1.02] active:scale-[0.98] lg:px-2"
      >
        <div
          class="flex justify-center items-center opacity-0 invisible left-0 truncate icon-slide-transition absolute group-[&.collapsed]/wrapper:opacity-100 group-[&.collapsed]/wrapper:visible group-[&.collapsed]/wrapper:translate-x-0 pl-3"
        >
          <Icon type="nav-ai" :width="24" :height="24" />
        </div>
        <div
          class="inline-flex truncate content-transition grow shrink basis-0 group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
        >
          <div
            class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
            :class="isActive('/ai') || isActive('/ai/loading') || isActive('/ai/company') ? css.label_active : css.label"
          >
            {{ $t('{app} AI', { app: $page.props.appName }) }}
          </div>
        </div>
      </Link>
      <Link
        v-if="hasPermissions(USER_PERMISSIONS.HR)"
        :href="route('hr-assistant.index')"
        :class="isActive('/hr-assistant') ? css.bg_active : css.bg_inactive"
        class="inline-flex items-center justify-start flex-shrink-0 w-full h-6 lg:h-[32px] relative px-6 menu-item-transition group overflow-hidden hover:scale-[1.02] active:scale-[0.98] lg:px-2"
      >
        <div
          class="flex justify-center items-center opacity-0 invisible left-0 truncate icon-slide-transition absolute group-[&.collapsed]/wrapper:opacity-100 group-[&.collapsed]/wrapper:visible group-[&.collapsed]/wrapper:translate-x-0 pl-3"
        >
          <Icon type="robot" :width="24" :height="24" />
        </div>
        <div
          class="inline-flex truncate content-transition grow shrink basis-0 group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible"
        >
          <div
            class="text-sm font-semibold truncate lg:text-xs whitespace-nowrap"
            :class="isActive('/hr-assistant') ? css.label_active : css.label"
          >
            {{ $t('AI Assistant') }}
          </div>
        </div>
      </Link>

      <div
        v-if="hasPermissions(USER_PERMISSIONS.HR)"
        class="flex-col hidden w-full min-h-0 px-2 lg:flex grow content-transition group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible group-[&.collapsed]/wrapper:h-0"
      >
        <div class="flex items-center justify-between gap-2 py-4 border-t border-divider">
          <button
            type="button"
            data-testid="recent-chats-toggle"
            class="flex items-center min-w-0 gap-2 group/chats"
            :aria-expanded="showRecentChats"
            @click="toggleRecentChats"
          >
            <h2 class="text-xs font-semibold truncate text-theme-primary">
              {{ $t('Recent chats') }}
            </h2>
            <span
              class="flex items-center justify-center w-2.5 h-2.5 transition-transform duration-300 ease-in-out shrink-0"
              :class="showRecentChats ? 'rotate-0' : 'rotate-180'"
            >
              <Icon class="text-theme-primary" type="chevron-up-small" color="currentColor" />
            </span>
          </button>
          <button
            @click="startNewChat"
            class="flex items-center justify-center px-2 py-1 text-xs font-semibold transition-colors rounded-lg shadow-card text-theme-primary bg-theme-neutral">
            <span class="truncate whitespace-nowrap">{{ $t('+ New conversation') }}</span>
          </button>
        </div>

        <div
          data-testid="recent-chats-panel"
          class="grid min-h-0 transition-[grid-template-rows,opacity] duration-300 ease-in-out"
          :class="
            showRecentChats
              ? 'flex-1 grid-rows-[1fr] opacity-100'
              : 'flex-none grid-rows-[0fr] opacity-0'
          "
        >
          <div
            ref="conversationsScroll"
            class="min-h-0 overflow-y-auto"
            @scroll.passive="rememberConversationsScroll"
          >
            <div
              v-if="conversations.length === 0"
              class="py-4 text-sm text-left text-theme-disabled"
            >
              {{ $t('No conversations yet') }}
            </div>
            <Link
              v-for="conv in conversations"
              :key="conv.id"
              :href="route('hr-assistant.show', conv.encid)"
              class="flex items-center justify-between py-1 text-xs transition-all group"
              :class="
                currentConversationId === conv.id ? 'text-theme-primary' : 'text-theme-secondary'
              "
            >
              <span class="flex-1 truncate">{{ conv.title || $t('New Conversation') }}</span>
              <button
                @click.prevent="openArchiveModal(conv)"
                class="p-1 transition-all opacity-0 text-theme-primary group-hover:opacity-100 hover:text-error"
              >
                <TrashIcon class="w-4 h-4" />
              </button>
            </Link>

            <div ref="conversationsSentinel" class="h-px"></div>

            <div v-if="isLoadingConversations" class="py-2 text-xs text-center text-theme-disabled">
              {{ $t('Loading...') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Teleported so the modal escapes the sidebar's stacking context -->
    <Teleport to="body">
      <ConfirmationModal
        :show="showArchiveModal"
        :title="$t('Archive Conversation')"
        :message="$t('Are you sure you want to archive this conversation?')"
        @confirmed="confirmArchiveConversation"
        @update:toggleModal="closeArchiveModal"
      />
    </Teleport>
  </div>
</template>
<script>
const conversationsState = {
  items: [],
  page: 1,
  hasMore: false,
  scrollTop: 0,
  expanded: false,
};
</script>
<script setup>
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import Dropdown from '@/Components/Dropdown.vue';
import OrgSwitcher from '@/Layouts/Navigation/OrgSwitcher.vue';
import Icon from '@/Icons/OptimizedIcons.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import { TrashIcon } from '@heroicons/vue/24/outline';
import {
  onMounted,
  onUnmounted,
  ref,
  computed,
  watch,
  nextTick,
} from 'vue';
import { Head, Link, usePage, router } from '@inertiajs/vue3';
import { hasPermissions } from '@/Utils/permissions.js';
import { formatBadgeCount } from '@/Utils/badgeCount.js';
import { USER_PERMISSIONS } from '@/constants.js';

const page = usePage();
const activeUrl = computed(() => page.url);
const isJobListUrl = computed(() => activeUrl.value.startsWith('/job-list/'));
const isHrAssistantUrl = computed(() => activeUrl.value.startsWith('/hr-assistant'));

const showJobs = ref(isJobListUrl.value);
const mobileJobsMenu = ref(null);

const toggleJobs = () => {
  showJobs.value = !showJobs.value;
};

// Sync showJobs state with Dropdown's internal state for icon rotation
const handleJobsDropdownChange = (isOpen) => {
  showJobs.value = isOpen;
};

const isActive = (url) => {
  return activeUrl.value === url;
};

const css = {
  bull_active: 'w-2 h-2 bg-main rounded-full',
  bull: 'w-1 h-1 bg-grey-500 dark:bg-grey-400 rounded-full whitespace-nowrap',
  label_active: 'text-theme-primary font-semibold',
  label_active_sub: 'text-black dark:text-theme-primary font-semibold',
  label: 'text-theme-disabled',
  bg_active: 'active',
  bg_inactive: 'bg-theme-paper bg-opacity-0 dark:bg-transparent transition-all',
};

const currentConversationId = computed(() => page.props.currentConversation?.id ?? null);

const conversations = ref(
  conversationsState.items.length
    ? [...conversationsState.items]
    : [...(page.props.hrAssistantConversations ?? [])]
);
const conversationsPage = ref(conversationsState.page);
const hasMoreConversations = ref(
  conversationsState.items.length
    ? conversationsState.hasMore
    : page.props.hrAssistantConversationsHasMore ?? false
);
const isLoadingConversations = ref(false);
const conversationsScroll = ref(null);
const conversationsSentinel = ref(null);
let conversationsObserver = null;

const cacheConversationsState = () => {
  conversationsState.items = [...conversations.value];
  conversationsState.page = conversationsPage.value;
  conversationsState.hasMore = hasMoreConversations.value;
};

watch(
  () => page.props.hrAssistantConversations,
  (shared) => {
    const head = [...(shared ?? [])];
    const headIds = new Set(head.map((conv) => conv.id));

    conversations.value = [...head, ...conversations.value.filter((conv) => !headIds.has(conv.id))];

    cacheConversationsState();
  }
);

const showRecentChats = ref(conversationsState.expanded);

const toggleRecentChats = () => {
  showRecentChats.value = !showRecentChats.value;
  conversationsState.expanded = showRecentChats.value;
};

const rememberConversationsScroll = (event) => {
  conversationsState.scrollTop = event.target.scrollTop;
};

const loadMoreConversations = () => {
  if (isLoadingConversations.value || !hasMoreConversations.value) {
    return;
  }

  isLoadingConversations.value = true;

  axios
    .get(route('hr-assistant.conversations'), {
      params: { page: conversationsPage.value + 1 },
    })
    .then((response) => {
      const loaded = response.data.conversations ?? [];
      const existingIds = new Set(conversations.value.map((conv) => conv.id));

      conversations.value.push(...loaded.filter((conv) => !existingIds.has(conv.id)));
      conversationsPage.value = response.data.page ?? conversationsPage.value + 1;
      hasMoreConversations.value = response.data.hasMore ?? false;
      cacheConversationsState();
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      isLoadingConversations.value = false;
    });
};

const showArchiveModal = ref(false);
const conversationToArchive = ref(null);

const startNewChat = () => {
  router.get(route('hr-assistant.index'));
};

const openArchiveModal = (conversation) => {
  conversationToArchive.value = conversation;
  showArchiveModal.value = true;
};

const closeArchiveModal = () => {
  showArchiveModal.value = false;
  conversationToArchive.value = null;
};

const confirmArchiveConversation = () => {
  if (conversationToArchive.value) {
    const archivedId = conversationToArchive.value.id;

    router.delete(route('hr-assistant.archive', conversationToArchive.value.encid));

    conversations.value = conversations.value.filter((conv) => conv.id !== archivedId);
    cacheConversationsState();
    closeArchiveModal();
  }
};

const countUnreadMessages = ref(0);

const getUnreadMessages = () => {
  axios
    .get(route('chat.unread-messages-jobs'))
    .then((response) => {
      const count = response.data.unreadMessagesCount ?? 0;
      if (count > 99) {
        countUnreadMessages.value = '99+';
      } else if (count > 32) {
        countUnreadMessages.value = '32+';
      } else {
        countUnreadMessages.value = count;
      }
    })
    .catch((error) => {
      console.error(error);
    });
};
const wrapper = ref(null);
const isCollapsed = ref(false);
const emit = defineEmits(['is-collapsed']);

const handleSidebarWidth = () => {
  // Only allow collapse on large screens and above
  if (window.innerWidth >= 1024) {
    wrapper.value.classList.toggle('collapsed');
    isCollapsed.value = !isCollapsed.value;
    showJobs.value = false;
    localStorage.setItem('sidebar-collapsed', isCollapsed.value);
    emit('is-collapsed', isCollapsed.value); // Emit AFTER updating state
  }
};

let removeNavigateListener = null;

onMounted(() => {
  // Restore sidebar state on large screens and above
  if (window.innerWidth >= 1024) {
    const saved = localStorage.getItem('sidebar-collapsed') === 'true';
    if (saved) {
      wrapper.value.classList.add('collapsed');
      isCollapsed.value = true;
    }
    // Notify parent of initial state
    emit('is-collapsed', isCollapsed.value);
  }

  getUnreadMessages();

  // Refetch unread count on every Inertia navigation
  removeNavigateListener = router.on('navigate', () => {
    getUnreadMessages();
  });

  // Click outside handler for mobile Jobs menu
  const handleClickOutside = (event) => {
    if (window.innerWidth < 1024) {
      // Only on mobile (md breakpoint is 768px)
      if (mobileJobsMenu.value && !mobileJobsMenu.value.contains(event.target)) {
        showJobs.value = false;
      }
    }
  };

  document.addEventListener('click', handleClickOutside);

  nextTick(() => {
    if (conversationsScroll.value) {
      conversationsScroll.value.scrollTop = conversationsState.scrollTop;
    }

    if (conversationsSentinel.value) {
      conversationsObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadMoreConversations();
            }
          });
        },
        {
          root: conversationsScroll.value,
          rootMargin: '0px 0px 200px 0px',
          threshold: 0,
        }
      );

      conversationsObserver.observe(conversationsSentinel.value);
    }
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    if (removeNavigateListener) {
      removeNavigateListener();
    }
    if (conversationsObserver) {
      conversationsObserver.disconnect();
      conversationsObserver = null;
    }
  });
});
</script>

<style scoped>
/* Custom cubic-bezier timing functions for smooth animations */

.sidebar-transition {
  transition: all 300ms cubic-bezier(0.165, 0.84, 0.44, 1);
  will-change: width, padding;
}

.content-transition {
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity, transform;
}

.icon-slide-transition {
  transition: opacity 350ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 350ms cubic-bezier(0.4, 0, 0.2, 1), visibility 350ms;
  will-change: opacity, transform;
  transform: translateX(-100%) translateZ(0);
  backface-visibility: hidden;
}

.button-transition {
  transition: all 200ms cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: transform, opacity;
}

.menu-item-transition {
  transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, background-color;
}

.sidebar-transition,
.content-transition,
.menu-item-transition {
  transform: translateZ(0);
  backface-visibility: hidden;
}

.group:hover .relative {
  animation: icon-bounce 400ms cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes icon-bounce {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.rotate-0,
.-rotate-90 {
  transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
